import {useContext, useState} from 'react';

import {Button, CircularProgress, Tooltip} from '@mui/material';
import {FileDownload} from '@mui/icons-material';

import AppContext from '../../context/AppContext';
import {exportDownloadCSV} from '../../utils/CsvHelper';
import {
  GroupGradesForCsvExportQueryRequest,
  GroupGradesForCsvExportResponse,
} from '../../api/responses/GroupsResponseTypes';
import {request, URLs} from '../../api';
import {fromDateToSmall} from '../../utils/date-utils';

type GeneralLeaderboardExportGradesButtonProps = {
  groupId: number | null;
  duringContest: boolean;
};

function GeneralLeaderboardExportGradesButton({groupId, duringContest}: GeneralLeaderboardExportGradesButtonProps) {
  const {dispatchError} = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);

  const prepareCsvAndExport = (data: GroupGradesForCsvExportResponse) => {
    const sortedContestIdsByName = Object.keys(data.contestsMetadata)
      .map(Number)
      .sort((a, b) => {
        const nameA = data.contestsMetadata[a].name.toLowerCase();
        const nameB = data.contestsMetadata[b].name.toLowerCase();
        return nameA.localeCompare(nameB);
      });

    const headers = ['email', ...sortedContestIdsByName.map((contestId) => data.contestsMetadata[contestId].name)];

    const preparedData = data.entries.map((row) => {
      const gradesByContestId: Record<number, string> = {};
      row.grades.forEach((gradeEntry) => {
        gradesByContestId[gradeEntry.contestId] = gradeEntry.grade.toString();
      });
      const rowObj: Record<string, string> = {
        email: row.email,
      };
      sortedContestIdsByName.forEach((contestId) => {
        rowObj[data.contestsMetadata[contestId].name] = gradesByContestId[contestId] ?? '0';
      });
      return rowObj;
    });

    exportDownloadCSV(
      headers,
      preparedData,
      `Grades for ${data.metadata.moduleName} - ${data.metadata.groupName} - ${fromDateToSmall(new Date())}`
    );
  };

  const exportGroupGrades = async () => {
    if (groupId) {
      setIsLoading(true);
      await request<GroupGradesForCsvExportQueryRequest, never, GroupGradesForCsvExportResponse>(
        URLs.getAllGroupGradesForCsvExport(groupId),
        {
          method: 'GET',
          params: {
            duringContest: duringContest ? 'true' : 'false',
          },
          successCallback(response) {
            prepareCsvAndExport(response);
            setIsLoading(false);
          },
          errorCallback() {
            dispatchError({errorMessage: 'There was an error. Please try again later'});
            setIsLoading(false);
          },
        }
      );
    }
  };

  return (
    <>
      <Tooltip
        title={
          !groupId
            ? 'You need to select a group before exporting the grades'
            : 'Exports all grades based on the selected filters'
        }
      >
        <span>
          <Button
            startIcon={isLoading ? <CircularProgress color="inherit" size="1.1rem" /> : <FileDownload />}
            size="small"
            color="primary"
            variant="contained"
            onClick={() => exportGroupGrades()}
            disabled={!groupId}
          >
            Export to CSV
          </Button>
        </span>
      </Tooltip>
    </>
  );
}

export default GeneralLeaderboardExportGradesButton;
